/**
 * The always-present quick log.
 *
 * Time tracking only works if logging is cheaper than not logging, so this sits
 * at the top of every page rather than living on the Time view. Two paths, both
 * one row:
 *
 *   - start a timer and stop it later
 *   - log a fixed amount that already happened (the common case for a call you
 *     have just hung up from)
 *
 * Work that belongs to no project is first-class here, not an afterthought:
 * pick "No project" and the bar swaps the task picker for a client and a
 * category, because an hour of email still needs to be attributable.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import { formatElapsed, useTimer } from "@/context/TimerContext";
import { useToast } from "@/context/ToastContext";
import { useAllItems, useAllPhases, useProjects } from "@/hooks/useData";
import { useRunningTimerEditor } from "@/hooks/useRunningTimerEditor";
import { effectiveRate, useClients, useTimeMutations } from "@/hooks/useTime";
import { itemProjectId } from "@/components/ItemRow";
import { KindSelect } from "@/components/KindPicker";
import { defaultKind, fitKind, kindChoices, kindFields, type EntryKind } from "@/lib/billing";
import { parseDuration } from "@/lib/timeRollup";
import { useActivities } from "@/lib/theme";
import { groupTasksByPhase } from "@/lib/taskOptions";
import { TaskCombo } from "@/components/TaskCombo";
import { useIsMobile } from "@/lib/useMediaQuery";

/** The amounts actually reached for; anything else goes in the h:mm box after them. */
const QUICK_MINUTES = [10, 15, 30, 60];
const AD_HOC = "__adhoc";
/** Sensible defaults for work with no project; free text is still allowed. */

export function QuickLogBar({ embedded = false, onDone }: {
  embedded?: boolean;
  /** Called once the bar has done its job — time logged, a timer started or
   *  stopped — so the panel holding it can close. */
  onDone?: () => void;
} = {}) {
  const timer = useTimer();
  const toast = useToast();
  const mobile = useIsMobile();
  const { data: projects = [] } = useProjects();
  const { data: clients = [] } = useClients();
  const { data: items = [] } = useAllItems();
  const { data: phases = [] } = useAllPhases();
  const { logEntry } = useTimeMutations();

  const [scope, setScope] = useState("");        // project id, or AD_HOC
  const [itemId, setItemId] = useState("");
  const [clientId, setClientId] = useState("");
  const [category, setCategory] = useState("");
  const [kindPick, setKindPick] = useState<EntryKind | null>(null);
  const [activities] = useActivities();
  const [note, setNote] = useState("");
  const [custom, setCustom] = useState("");

  const adHoc = scope === AD_HOC;
  const projectId = adHoc || !scope ? undefined : scope;

  const taskGroups = useMemo(
    () => groupTasksByPhase(items, phases, projectId, itemProjectId),
    [items, phases, projectId],
  );

  const selectable = projects.filter((p) => p.status !== "archived");

  /** Picking an activity fills the note, unless you typed one of your own. */
  function chooseActivity(next: string) {
    setCategory(next);
    const written = note.trim();
    if (!written || activities.includes(written)) setNote(next);
  }

  // Billable / No charge / Internal, defaulting from the project (lib/billing).
  const kindProject = projects.find((p) => p.id === projectId);
  const kindHasClient = !!(effectiveRate(kindProject, clients).clientId || clientId);
  const kindChoicesNow = kindChoices(kindProject, kindHasClient);
  const kind = fitKind(kindPick ?? defaultKind(kindProject, kindHasClient), kindProject, kindHasClient);

  function resolve() {
    const project = projects.find((p) => p.id === projectId);
    const derived = effectiveRate(project, clients);
    const direct = clientId ? clients.find((c) => c.id === clientId) : undefined;
    const rate = derived.rate ?? direct?.defaultRate ?? null;
    const k = kindFields(kind, rate);
    return {
      rate: k.rate,
      currency: derived.currency || direct?.currency || "USD",
      clientId: derived.clientId ?? direct?.id ?? null,
      billable: k.billable, internal: k.internal,
    };
  }

  function reset() {
    setNote("");
    setItemId("");
    setCustom("");
  }

  function logCustom() {
    const minutes = parseDuration(custom);
    if (minutes === null || minutes <= 0) {
      toast("Enter a length like 1:30, 90 or 1.5h");
      return;
    }
    void log(minutes);
  }

  function start() {
    if (!scope) { toast("Pick a project, or “No project” for ad-hoc time"); return; }
    const r = resolve();
    timer.start({
      projectId, itemId: itemId || undefined, clientId: r.clientId ?? undefined,
      category: category || undefined, note, kind,
    });
    reset();
    onDone?.();
  }

  async function log(minutes: number) {
    if (!scope) { toast("Pick a project, or “No project” for ad-hoc time"); return; }
    const end = new Date();
    const start = new Date(end.getTime() - minutes * 60_000);
    const r = resolve();
    try {
      await logEntry.mutateAsync({
        projectId, itemId: itemId || undefined,
        category: category || undefined,
        start: start.toISOString(), end: end.toISOString(),
        durationSeconds: minutes * 60,
        note, billable: r.billable, internal: r.internal, source: "manual",
        rate: r.rate, currency: r.currency, clientId: r.clientId,
      });
      reset();
      toast(`Logged ${minutes < 60 ? `${minutes}m` : `${minutes / 60}h`}`);
      onDone?.();
    } catch {
      toast("Could not save that entry");
    }
  }

  if (timer.running) return <RunningEditor embedded={embedded} mobile={mobile} onDone={onDone} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* What you're doing comes first and gets the room: it is what the
          entry is called everywhere it shows up later. The cursor starts here
          (TopNav focuses .ql-note when the panel opens). */}
      <input
        className="ql-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="What are you working on?"
        aria-label="What are you working on?"
        style={{
          width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 16,
          border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
        }}
      />

      <div style={embedded ? barEmbedded : bar}>
        {/* A <select> sizes itself to its LONGEST OPTION, so one long project
            name widened this control and pushed the whole bar onto a second row.
            Allowing it to shrink lets the browser ellipsise the displayed label
            instead; the full name is still in the open dropdown. */}
        <select
          value={scope}
          onChange={(e) => { setScope(e.target.value); setItemId(""); setKindPick(null); }}
          style={{ ...select(mobile), flex: "1 1 130px", minWidth: mobile ? 140 : 0, maxWidth: 190 }}
          aria-label="Project"
        >
          <option value="">— Project</option>
          <option value={AD_HOC}>No project (ad-hoc)</option>
          {selectable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        {adHoc ? (
          <>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={select(mobile)} aria-label="Client">
              <option value="">— No client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </>
        ) : (
          <TaskCombo value={itemId} onChange={setItemId} groups={taskGroups} disabled={!projectId} style={{ ...select(mobile), flex: "1 1 110px", minWidth: mobile ? 120 : 0, maxWidth: 190 }} />
        )}

        {activities.length > 0 && (
          <select
            value={category}
            onChange={(e) => chooseActivity(e.target.value)}
            aria-label="Activity"
            style={{ ...select(mobile), maxWidth: 150, color: category ? "var(--ink)" : "var(--muted)" }}
          >
            <option value="">Activity…</option>
            {activities.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        )}

        <KindSelect value={kind} choices={kindChoicesNow} onChange={setKindPick}
          style={{ ...select(mobile), maxWidth: 130 }} />

      </div>

      <div style={embedded ? barEmbedded : bar}>
        <button onClick={start} style={primaryBtn(mobile)}>Start</button>
        {QUICK_MINUTES.map((m) => (
          <button key={m} onClick={() => void log(m)} style={ghostBtn(mobile)} title={`Log ${m} minutes just now`}>
            {m < 60 ? `${m}m` : `${m / 60}h`}
          </button>
        ))}
        <input
          className="ql-custom"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") logCustom(); }}
          onBlur={() => custom.trim() && logCustom()}
          placeholder="h:mm"
          title="Type a length as h:mm (or 90, 1.5h) and press Enter"
          aria-label="Custom duration"
          style={{ ...input(mobile), width: mobile ? 84 : 66, minWidth: mobile ? 84 : 66, textAlign: "center" }}
        />
      </div>
    </div>
  );
}

/**
 * The running timer, editable — the desktop twin of the phone's running sheet.
 * Start now, fix the details while it runs: project, task, kind, description,
 * even when it started.
 */
function RunningEditor({ embedded, mobile, onDone }: { embedded: boolean; mobile: boolean; onDone?: () => void }) {
  const ed = useRunningTimerEditor();
  const { timer } = ed;
  // Clicking away closes the panel without a blur reaching the inputs, so
  // whatever is typed is committed as it unmounts — unless it was stopped or
  // discarded, when there is no timer left to write to.
  const finished = useRef(false);
  const commitRef = useRef(ed.commit);
  commitRef.current = ed.commit;
  useEffect(() => () => { if (!finished.current) commitRef.current(); }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
        <span className="mono" style={{ fontSize: 26, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
          {formatElapsed(timer.seconds)}
        </span>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--muted)" }}>
          started
          <input
            type="time"
            value={ed.startText}
            onChange={(e) => ed.setStartText(e.target.value)}
            onBlur={ed.commitStart}
            onKeyDown={(e) => { if (e.key === "Enter") ed.commitStart(); }}
            aria-label="Start time"
            style={{ ...input(mobile), minWidth: 0, width: 110 }}
          />
        </label>
      </div>

      <input
        value={ed.note}
        onChange={(e) => ed.setNote(e.target.value)}
        onBlur={ed.commitNote}
        onKeyDown={(e) => { if (e.key === "Enter") ed.commitNote(); }}
        placeholder="What are you working on?"
        aria-label="Description"
        style={{
          width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 16,
          border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
        }}
      />

      <div style={embedded ? barEmbedded : bar}>
        <select
          value={timer.meta.projectId ?? ""}
          onChange={(e) => ed.setProject(e.target.value)}
          style={{ ...select(mobile), flex: "1 1 130px", minWidth: 0, maxWidth: 190 }}
          aria-label="Project"
        >
          <option value="">No project (ad-hoc)</option>
          {ed.selectable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <TaskCombo
          value={timer.meta.itemId ?? ""}
          onChange={ed.setTask}
          groups={ed.taskGroups}
          disabled={!timer.meta.projectId}
          style={{ ...select(mobile), flex: "1 1 110px", minWidth: 0, maxWidth: 190 }}
        />
        <KindSelect value={ed.kind} choices={ed.choices} onChange={ed.setKind} style={{ ...select(mobile), maxWidth: 130 }} />
      </div>

      <div style={embedded ? barEmbedded : bar}>
        <button onClick={() => { finished.current = true; ed.stopAndLog(); onDone?.(); }} style={primaryBtn(mobile)}>Stop &amp; log</button>
        <button onClick={() => { finished.current = true; void timer.discard(); onDone?.(); }} style={ghostBtn(mobile)}>Discard</button>
      </div>
    </div>
  );
}

const barBase: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
};
/** Standalone: its own strip. Embedded: sits inside the header's top row. */
const bar: React.CSSProperties = {
  ...barBase,
  padding: "8px 16px", borderBottom: "1px solid var(--line)",
  background: "var(--surface2)", position: "sticky", top: 0, zIndex: 30,
};
const barEmbedded: React.CSSProperties = { ...barBase, flex: 1, minWidth: 0 };
/**
 * Desktop padding lands these at 30px, under the 44pt minimum — on a phone,
 * on the control you are most likely to hit one-handed. `barBase` already
 * wraps, which matters here: at 44pt the bar becomes two rows on a 390pt
 * screen, and without wrapping the last chip goes off-screen while
 * `flex: 1; min-width: 0` collapses the task select to a sliver.
 */
const TAP = 44;

const select = (mobile: boolean): React.CSSProperties => ({
  padding: mobile ? "10px 10px" : "6px 9px", minHeight: mobile ? TAP : undefined,
  borderRadius: 8, fontSize: mobile ? 14 : 12.5, maxWidth: 190,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
});
const input = (mobile: boolean): React.CSSProperties => ({
  padding: mobile ? "10px 10px" : "6px 9px", minHeight: mobile ? TAP : undefined,
  borderRadius: 8, fontSize: mobile ? 14 : 12.5, minWidth: 100,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
});
const primaryBtn = (mobile: boolean): React.CSSProperties => ({
  padding: mobile ? "0 18px" : "6px 14px", minHeight: mobile ? TAP : undefined,
  borderRadius: 999, border: "none",
  background: "var(--accent)", color: "var(--onAccent)", fontSize: mobile ? 14 : 12.5, cursor: "pointer",
});
const ghostBtn = (mobile: boolean): React.CSSProperties => ({
  padding: mobile ? "0 14px" : "6px 11px", minHeight: mobile ? TAP : undefined,
  borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: mobile ? 14 : 12.5, cursor: "pointer",
});
